/**
 * Web analytics (Umami) — acquisition only.
 *
 * Loaded through our own origin (`/u/s.js`, proxied by `server/analytics.ts`)
 * so ad blockers, which a founder/dev audience runs heavily, do not erase the
 * numbers. No cookie, no persistent id, no cross-site tracking, so no consent
 * banner is required — keep it that way: never pass anything identifying to
 * `track()`.
 *
 * Absent `VITE_UMAMI_WEBSITE_ID`, this is inert. That is the normal state in
 * dev and in tests.
 */

/** Where the proxied tracker lives. The vendor domain is never touched. */
const SCRIPT_SRC = '/u/s.js';
/**
 * The tracker builds its endpoint as `${data-host-url}/api/send` and otherwise
 * falls back to the vendor gateway. Relative keeps the beacon on whichever
 * host served the page, so prod / the Fly origin / a preview all just work.
 */
const HOST_URL = '/u';

type UmamiGlobal = { track?: (event: string, data?: Record<string, unknown>) => void };

export function initAnalytics(): void {
  const websiteId = import.meta.env.VITE_UMAMI_WEBSITE_ID;
  if (!websiteId) return;
  // Idempotent against the DOM rather than a module flag, so a re-render or a
  // StrictMode double-invoke cannot double-count every pageview.
  if (document.querySelector('script[data-website-id]')) return;

  const tag = document.createElement('script');
  tag.defer = true;
  tag.src = SCRIPT_SRC;
  tag.setAttribute('data-website-id', websiteId);
  tag.setAttribute('data-host-url', HOST_URL);
  document.head.appendChild(tag);
}

/**
 * Record a conversion step. Pageviews (including SPA route changes) are already
 * automatic — the tracker hooks pushState/replaceState itself.
 *
 * Safe to call unconditionally: no tracker, a blocked script, or a throwing
 * extension all degrade to a no-op. Analytics never breaks a user flow.
 */
export function track(event: string, data?: Record<string, unknown>): void {
  try {
    (window as { umami?: UmamiGlobal }).umami?.track?.(event, data);
  } catch {
    /* blocked or broken tracker — losing the event is the acceptable outcome */
  }
}
